import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import Pagination from '../../shared/components/Pagination';
import { getUserActivities, getUserActivitySummary, getUserActivity } from './activitiesApi';
import { UserActivity, UserActivitySummary } from '../../shared/types';

const CATEGORY_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: 'security', label: 'Security' },
  { value: 'account', label: 'Account' },
  { value: 'trading', label: 'Trading' },
  { value: 'finance', label: 'Finance' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'user.registered', label: 'Registration' },
  { value: 'user.login', label: 'Login' },
  { value: 'user.password_changed', label: 'Password changed' },
  { value: 'user.email_verified', label: 'Email verified' },
  { value: 'user.email_changed', label: 'Email changed' },
  { value: 'user.2fa_enabled', label: '2FA enabled' },
  { value: 'user.2fa_disabled', label: '2FA disabled' },
  { value: 'offer.created', label: 'Offer created' },
  { value: 'offer.updated', label: 'Offer updated' },
  { value: 'offer.cancelled', label: 'Offer cancelled' },
  { value: 'trade.created', label: 'Trade created' },
  { value: 'trade.paid', label: 'Trade paid' },
  { value: 'trade.released', label: 'Trade released' },
  { value: 'trade.cancelled', label: 'Trade cancelled' },
  { value: 'trade.disputed', label: 'Trade disputed' },
  { value: 'withdrawal.requested', label: 'Withdrawal requested' },
  { value: 'withdrawal.confirmed', label: 'Withdrawal confirmed' },
  { value: 'withdrawal.completed', label: 'Withdrawal completed' },
  { value: 'withdrawal.failed', label: 'Withdrawal failed' },
  { value: 'withdrawal.rejected', label: 'Withdrawal rejected' },
  { value: 'deposit.received', label: 'Deposit received' },
  { value: 'swap.completed', label: 'Swap completed' },
];

const getCategoryBadge = (category: string) => {
  switch (category) {
    case 'security': return 'badge-danger';
    case 'account': return 'badge-info';
    case 'trading': return 'badge-warning';
    case 'finance': return 'badge-success';
    default: return 'badge-info';
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'completed': return 'badge-success';
    case 'pending': return 'badge-warning';
    case 'failed': return 'badge-danger';
    default: return 'badge-info';
  }
};

const UserActivities: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialUserId = searchParams.get('userId');

  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [meta, setMeta] = useState<{ current_page: number; last_page: number; per_page: number; total: number } | null>(null);
  const [summary, setSummary] = useState<UserActivitySummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const [userIdInput, setUserIdInput] = useState(initialUserId || '');
  const [userId, setUserId] = useState<number | null>(initialUserId ? parseInt(initialUserId, 10) : null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [selectedActivity, setSelectedActivity] = useState<UserActivity | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const loadActivities = useCallback(async (uid: number, page: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const params: { per_page: number; page: number; category?: string; type?: string } = {
        per_page: 20,
        page,
      };
      if (categoryFilter) params.category = categoryFilter;
      if (typeFilter) params.type = typeFilter;

      const response = await getUserActivities(uid, params);
      setActivities(response.data);
      setMeta(response.meta);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string; message?: string } } }).response?.data?.error
          ?? (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Failed to load activities'
        : 'Failed to load activities';
      setError(msg);
      setActivities([]);
      setMeta(null);
    } finally {
      setIsLoading(false);
    }
  }, [categoryFilter, typeFilter]);

  const loadSummary = useCallback(async (uid: number) => {
    try {
      const data = await getUserActivitySummary(uid, 10);
      setSummary(data);
    } catch {
      setSummary(null);
    }
  }, []);

  useEffect(() => {
    if (userId) {
      loadActivities(userId, currentPage);
    }
  }, [userId, currentPage, loadActivities]);

  useEffect(() => {
    if (userId) {
      loadSummary(userId);
    }
  }, [userId, loadSummary]);

  const handleSearch = () => {
    const id = parseInt(userIdInput.trim(), 10);
    if (!id || id <= 0) return;
    setUserId(id);
    setCurrentPage(1);
  };

  const handleCategoryChange = (value: string) => {
    setCategoryFilter(value);
    setCurrentPage(1);
  };

  const handleTypeChange = (value: string) => {
    setTypeFilter(value);
    setCurrentPage(1);
  };

  const openDetailModal = async (activity: UserActivity) => {
    if (!userId) return;
    try {
      const response = await getUserActivity(userId, activity.id);
      setSelectedActivity(response.data);
      setIsDetailModalOpen(true);
    } catch {
      setSelectedActivity(activity);
      setIsDetailModalOpen(true);
    }
  };

  const formatJson = (data: Record<string, unknown> | null) => {
    if (!data || Object.keys(data).length === 0) return null;
    return JSON.stringify(data, null, 2);
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-white">User Activities</h1>
          <p className="text-slate-400 mt-1">Track all user actions on the platform</p>
        </div>

        <div className="card p-4">
          <label className="block text-xs font-medium text-slate-400 mb-2">Search by User ID</label>
          <div className="flex gap-3">
            <input
              type="text"
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearch())}
              placeholder="Enter user ID, e.g. 1"
              className="input py-2 max-w-xs"
            />
            <button onClick={handleSearch} className="btn btn-primary py-2">
              Search
            </button>
          </div>
        </div>

        {userId && summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="card p-4">
              <p className="text-xs text-slate-400 uppercase tracking-wider">Total</p>
              <p className="text-2xl font-bold text-white mt-1">{summary.stats.total}</p>
            </div>
            {Object.entries(summary.stats.by_category).map(([cat, count]) => (
              <div key={cat} className="card p-4">
                <p className="text-xs text-slate-400 uppercase tracking-wider">{cat}</p>
                <p className="text-2xl font-bold text-white mt-1">{count}</p>
              </div>
            ))}
          </div>
        )}

        {userId && (
          <>
            <div className="card p-4 flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Category</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="input py-2 w-44"
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Activity type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="input py-2 w-56"
                >
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {(categoryFilter || typeFilter) && (
                <button
                  onClick={() => { setCategoryFilter(''); setTypeFilter(''); setCurrentPage(1); }}
                  className="btn btn-secondary py-2"
                >
                  Clear filters
                </button>
              )}
            </div>

            <div className="card overflow-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
                </div>
              ) : error ? (
                <div className="p-6 text-center">
                  <p className="text-red-400">{error}</p>
                </div>
              ) : !activities.length ? (
                <div className="p-12 text-center text-slate-400">
                  No activities found for user #{userId}.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-800/50">
                        <tr>
                          <th className="table-header">Activity</th>
                          <th className="table-header">Category</th>
                          <th className="table-header">Status</th>
                          <th className="table-header">IP Address</th>
                          <th className="table-header">Date</th>
                          <th className="table-header text-right">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {activities.map((activity) => (
                          <tr key={activity.id} className="hover:bg-slate-800/30 transition-colors">
                            <td className="table-cell">
                              <div>
                                <p className="font-medium text-white">{activity.title}</p>
                                <p className="text-xs text-slate-500 mt-0.5">{activity.activity_type}</p>
                              </div>
                            </td>
                            <td className="table-cell">
                              <span className={`badge ${getCategoryBadge(activity.category)}`}>
                                {activity.category}
                              </span>
                            </td>
                            <td className="table-cell">
                              <span className={`badge ${getStatusBadge(activity.status)}`}>
                                {activity.status}
                              </span>
                            </td>
                            <td className="table-cell">
                              <span className="font-mono text-xs text-slate-400">
                                {activity.ip_address || '-'}
                              </span>
                            </td>
                            <td className="table-cell">
                              <div>
                                <p className="text-white">
                                  {new Date(activity.created_at).toLocaleDateString()}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {new Date(activity.created_at).toLocaleTimeString()}
                                </p>
                              </div>
                            </td>
                            <td className="table-cell text-right">
                              <button
                                onClick={() => openDetailModal(activity)}
                                className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-all"
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {meta && meta.last_page > 1 && (
                    <div className="px-6 py-4 border-t border-slate-800">
                      <Pagination
                        currentPage={currentPage}
                        lastPage={meta.last_page}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {!userId && (
          <div className="card p-12 text-center">
            <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl text-slate-500">&#9673;</span>
            </div>
            <p className="text-slate-400 text-lg">Enter a user ID to view their activity</p>
            <p className="text-slate-500 text-sm mt-1">All logins, trades, withdrawals, security changes and more</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title="Activity Details"
      >
        {selectedActivity && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Activity</p>
                <p className="text-white font-medium">{selectedActivity.title}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Type</p>
                <p className="text-white font-mono text-sm">{selectedActivity.activity_type}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Category</p>
                <span className={`badge ${getCategoryBadge(selectedActivity.category)}`}>
                  {selectedActivity.category}
                </span>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Status</p>
                <span className={`badge ${getStatusBadge(selectedActivity.status)}`}>
                  {selectedActivity.status}
                </span>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">IP Address</p>
                <p className="text-white font-mono text-sm">{selectedActivity.ip_address || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Date</p>
                <p className="text-white">
                  {new Date(selectedActivity.created_at).toLocaleString()}
                </p>
              </div>
            </div>

            {selectedActivity.description && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Description</p>
                <p className="text-slate-300 text-sm">{selectedActivity.description}</p>
              </div>
            )}

            {formatJson(selectedActivity.metadata) && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Metadata</p>
                <pre className="bg-slate-800 rounded-lg p-3 text-xs text-slate-300 overflow-auto max-h-48 font-mono">
                  {formatJson(selectedActivity.metadata)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default UserActivities;
